import { contactPipelineInput } from 'app/lib/contactPipelineInput'
/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useEffect, useMemo, useRef, useState } from 'react'
import { ACTION_CENTER_FLAG, isSystemPipelineId } from 'app/components/action-center/systemPipelines'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useListTraversal } from 'app/hooks/useListTraversal'
import { ContactJson, PipelineType, StageType, Workspace } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useUser, useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { debug, logError } from 'lib/logging'
import { Jsonify } from 'lib/toJSON'
import { AddPipeline } from 'app/components/pipelines/AddPipeline'
import { useLocationFilter } from 'app/hooks/useLocationFilter'
import { useContactStore, usePipelineStore } from 'app/stores/StoreContext'
import { contactLocationClause, resolveContactLocationFilter } from './locationFilter'
import { contactMatchesQuery, contactNameTerms } from 'lib/contactSearch'

export type UsePipelinesPageReturn = {
  me: ReturnType<typeof useUser>
  workspace: Jsonify<Workspace> | undefined
  contacts: ContactJson[]
  activePipeline: PipelineType | undefined
  focusedContact: ContactJson | undefined
  openDrawer: boolean
  contactTraversal: ReturnType<typeof useListTraversal<ContactJson>>
  createPipeline: (data: { [k: string]: FormDataEntryValue }) => Promise<void>
  updatePipelineStage: (stage: StageType, index: number) => Promise<void | { error?: string }>
  deleteStage: (name: string) => void
  addContactToPipeline: (contact: ContactJson, stage: StageType) => void
  addContactsToPipeline: (contacts: ContactJson[], stage: StageType) => void
  contactHandler: (contactId: string) => Promise<void>
  updateContactStage: (contactId: string, stage: StageType) => void
  removeContact: (contactId: string) => void
  closeDrawerHandler: () => void
  loadContact: (contact?: ContactJson) => void
  openCreateModal: () => void
}

export function usePipelinesPage(): UsePipelinesPageReturn {
  const me = useUser()
  const workspace: Jsonify<Workspace> | undefined = useWorkspace()
  const navigation = useNavigate()
  const [searchParams] = useSearchParams()
  const contactStore = useContactStore()
  const pipelineStore = usePipelineStore()
  const actionCenterEnabled = useFeatureToggle(ACTION_CENTER_FLAG)
  const [contacts, setContacts] = useState<ContactJson[]>([])
  // Scope (tab + resolved location clause) the loaded `contacts` belong to. The board only
  // renders `contacts` when this matches the current scope, so a stale tab/filter — or an
  // in-flight fetch that later fails — never leaves out-of-scope contacts on screen.
  const [loadedScope, setLoadedScope] = useState<string>(``)
  const [activePipeline, setActivePipeline] = useState<PipelineType>()
  const activePipelineRef = useRef(activePipeline)
  activePipelineRef.current = activePipeline
  const [focusedContact, setFocusedContact] = useState<ContactJson | undefined>()
  const [openDrawer, setOpenDrawer] = useState<boolean>(() => searchParams.has(`drawer`))
  // Monotonic token so an earlier, superseded contacts fetch can't overwrite a newer one.
  const contactsFetchId = useRef(0)

  // Location filter state, shared with the board's LocationSelector via the URL params.
  const { locationIds, locationOptions, isLoading: locationsLoading } = useLocationFilter()
  const locationFilter = useMemo(
    () => resolveContactLocationFilter({ locationIds, locationOptions, isLoading: locationsLoading }),
    [locationIds, locationOptions, locationsLoading],
  )
  const locationClause = useMemo(() => contactLocationClause(locationFilter), [locationFilter])
  // While the filter is still resolving we hold off querying so we never briefly fetch
  // every location's contacts before an integrationStatus selection resolves.
  const locationReady = locationFilter.state !== `pending`
  const activeTab = searchParams.get(`tab`)
  // Free-text search from the board's <Search /> box, which writes the `query` URL param.
  const query = searchParams.get(`query`) ?? ``
  const nameTerms = useMemo(() => contactNameTerms(query), [query])
  // Everything narrowing the pipeline's contacts, as ES terms to AND together.
  const filterTerms = useMemo(() => [locationClause, ...nameTerms].filter(Boolean), [locationClause, nameTerms])
  // The scope the board should currently be showing. `pending` never matches a fetched
  // scope, so the previous selection's contacts are suppressed while a filter resolves.
  const scopeKey = filterTerms.join(` AND `)
  const currentScope = !activeTab ? `` : locationReady ? `${activeTab}::${scopeKey}` : `${activeTab}::pending`

  // Fetch pipeline data
  useEffect(() => {
    const tab = searchParams.get(`tab`)
    // Invalidate any in-flight contacts fetch on every tab/filter/loading transition, so a
    // request for a superseded scope can never populate the board once its filter changed.
    const fetchId = ++contactsFetchId.current

    pipelineStore
      .fetchList()
      .then((result) => {
        const data = result.data as any
        // The store always carries the system pipelines; whether they are tabs on this
        // page is a separate question. Without the Action Center they are not: their
        // boards sit behind `FeatureRoute`, and this legacy page has none of their
        // handling anyway.
        const all = data?.pipelines ?? []
        const pipelines = actionCenterEnabled ? all : all.filter((p: any) => !isSystemPipelineId(p.id))
        // Explicit rather than resting on array position: PipelineStore.fetchList leads with
        // the system pipelines (they sort to the top of the Pipelines nav too), but that
        // ordering is a separate, independently editable function. Naming the practice's own
        // pipeline here means the merge's ordering can never silently default this legacy
        // board (which has none of the Action Center's system-pipeline handling) onto a
        // system pipeline.
        const pipeline = tab
          ? pipelines.find((p: any) => p.id === tab)
          : (pipelines.find((p: any) => !isSystemPipelineId(p.id)) ?? pipelines[0])
        setActivePipeline(pipeline)
      })
      .catch((err) => logError(`Error fetching pipelines`, err))

    // Hold off while the filter is still resolving; the display gates on scope, so the
    // previous selection's contacts are already suppressed until this resolves.
    //
    // A system pipeline's board owns its own contacts query (ContactBoardComponent /
    // TreatmentBoardComponent, rendered by PipelinesComponent instead of PipelineLayout).
    // This page's `pipelines.id:"<tab>"` search finds memberships only: for system:leads
    // that is always zero (leadStatus-backed, never a pipeline membership; see
    // systemPipelines.ts), and for system:treatment it is a sliver of the population, most
    // of whom have no membership yet. Fetching here would just be a pointless request
    // feeding a `contacts` list nothing reads once the board renders.
    if (!tab || !locationReady || isSystemPipelineId(tab)) return

    // Fetch contacts for the active pipeline, narrowed by any selected locations and the
    // search box. Searching server-side rather than filtering `contacts` in the browser
    // keeps results honest past the API's default page of 1000 contacts.
    const scope = `${tab}::${scopeKey}`
    // Quoted: a reserved id like `system:hygiene` carries a colon, which
    // Lucene reads as a field separator, so bare interpolation is a different
    // query than it looks. Harmless for a hex id, essential for a system one.
    const search = [`pipelines.id:"${tab}"`, ...filterTerms].join(` AND `)
    contactStore
      .fetchList({ search })
      .then((result) => {
        // Ignore results from a fetch that a newer filter/tab change has superseded.
        if (fetchId !== contactsFetchId.current) return
        const data = result.data as any
        const hits = data?.contacts?.hits ?? data?.hits ?? []
        setContacts(hits)
        setLoadedScope(scope)
        // Clear pending if ES already has them all
        const pending = contactStore.pendingPipelineContacts.get(tab) ?? []
        const hitIds = new Set(hits.map((c: any) => c.id))
        if (pending.length > 0 && pending.every((c) => hitIds.has(c.id))) {
          contactStore.clearPendingPipelineContacts(tab)
        }
      })
      .catch((err) => {
        if (fetchId !== contactsFetchId.current) return
        logError(`Error fetching pipeline contacts`, err)
        // Don't leave the previous scope's contacts on screen after a failed refetch.
        setContacts([])
        setLoadedScope(scope)
      })
  }, [searchParams.toString(), scopeKey, locationReady, actionCenterEnabled])

  // Reactively merge pending contacts (added via the drawer) that ES has not yet
  // indexed. The page is wrapped in observer(), so reading the observable here
  // keeps the list live without a refresh.
  const tab = activeTab
  const pendingForTab = tab ? (contactStore.pendingPipelineContacts.get(tab) ?? []) : []
  const displayedContacts = useMemo(() => {
    // Only surface fetched contacts that belong to the current tab + filter scope; while a
    // newer scope is loading (or after a failed refetch) `contacts` are held back.
    const scopedContacts = loadedScope === currentScope ? contacts : []
    // Keep pending contacts consistent with the active location filter and search so
    // locally-added (not-yet-indexed) contacts don't bypass either. When the location
    // filter matches nothing (or is still resolving) surface no pending contacts.
    const pendingInLocation =
      locationFilter.state === `inactive`
        ? pendingForTab
        : locationFilter.state === `ids`
          ? pendingForTab.filter((c) => c.locationId && locationFilter.ids.includes(c.locationId))
          : []
    const filteredPending = pendingInLocation.filter((c) => contactMatchesQuery(c, query))
    if (filteredPending.length === 0) return scopedContacts
    const contactIds = new Set(scopedContacts.map((c) => c.id))
    return [...scopedContacts, ...filteredPending.filter((c) => !contactIds.has(c.id))]
  }, [contacts, loadedScope, currentScope, pendingForTab, locationFilter, query])

  const contactTraversal = useListTraversal<ContactJson>(displayedContacts ?? [], focusedContact, (item) => {
    // oxlint-disable-next-line react/immutability -- existing closure or mutable API pattern is intentional
    if (item.id) bg(contactHandler(item.id))
  })

  useEffect(() => {
    if (me?.workspaceId && workspace?.id && me.workspaceId !== workspace.id) {
      navigation(`/pipelines?wId=${me.workspaceId}`, { replace: true })
    }
  }, [me?.workspaceId, workspace?.id])

  const createPipeline = async (formData: { [k: string]: FormDataEntryValue }) => {
    const result = await pipelineStore.create({
      name: formData.name as string,
      stages:
        formData.stage && formData.color
          ? [{ name: formData.stage as string, color: formData.color as string, order: 1 }]
          : undefined,
    })
    if (result.error) {
      debug(`pipeline`, `error`, result.error.message)
      notifyError(
        `There was an error creating your pipeline: ${formData.name}. ${result.error.message}`,
        `pipeline-create-error`,
      )
    } else {
      const pipeline = pipelineStore.current
      notifySuccess(`Pipeline saved successfully`, `pipeline-create`)
      closeModal()
      window.FS?.(`trackEvent`, { name: `pipelines` })
      if (pipeline) {
        navigation(
          `${location.pathname}?tab=${pipeline.id}&${searchParams.has(`wId`) ? `wId=${searchParams.get(`wId`)}` : ``}`,
        )
        setActivePipeline(pipeline)
      }
    }
  }

  const updatePipelineStage: (stage: StageType, index: number) => Promise<void | { error?: string }> = async (
    stage,
    index,
  ) => {
    const pipeline = activePipelineRef.current
    const currentStages = pipeline?.stages ?? []
    const isAdding = index >= currentStages.length || index === -1
    // System pipelines are frontend constants (systemPipelines.ts), not database records: the
    // Action Center's boards derive their columns, and their exit logic, from these exact stage
    // names. Colour carries none of that weight, so a same-name edit (colour only) still goes
    // through below; only an add or an actual rename is refused.
    const isRenaming = !isAdding && currentStages[index]?.name !== stage.name
    if (isSystemPipelineId(pipeline?.id) && (isAdding || isRenaming)) {
      notifyError(
        `${pipeline?.name ?? `This pipeline`}’s stages are part of the Action Center and can’t be added or renamed`,
        `system-pipeline-stage-locked`,
      )
      closeModal()
      return
    }
    const hasDuplicate = currentStages.some(
      (s, i) => s.name.toLowerCase() === stage.name.toLowerCase() && (isAdding || i !== index),
    )
    if (hasDuplicate) {
      return { error: `name-duplicated` }
    }
    // Preserve each stage's stored order; stages have user-defined custom
    // orders, so we must not renumber them by array position. PipelineStageInput
    // requires a non-null order, so fall back to the array position only when a
    // stage has no stored order (e.g. a newly added stage).
    const updatedStages = isAdding
      ? [...currentStages, stage].map((s, i) => ({
          name: s.name,
          color: s.color ?? `pipelines-silver`,
          order: s.order ?? i + 1,
        }))
      : currentStages.map((s, i) =>
          i === index
            ? { name: stage.name, color: stage.color ?? `pipelines-silver`, order: stage.order ?? s.order ?? i + 1 }
            : { name: s.name, color: s.color ?? `pipelines-silver`, order: s.order ?? i + 1 },
        )

    const updateResult = await pipelineStore.update(pipeline!.id!, {
      stages: updatedStages,
    })
    if (updateResult.error) {
      debug(`pipeline`, `error`, updateResult.error.message)
      notifyError(
        `There was an error adding the stage ${stage.name} to your pipeline. ${updateResult.error.message}`,
        `stage-update-error`,
      )
    } else {
      setActivePipeline({ ...pipeline!, stages: updatedStages })
      if (!isAdding && currentStages[index]?.name !== stage.name) {
        const oldName = currentStages[index].name
        const affectedContacts = contacts.filter((c) =>
          c.pipelines?.some((p) => p.id === pipeline!.id && p.stage === oldName),
        )
        setContacts((prev) =>
          prev.map((c) => ({
            ...c,
            pipelines: c.pipelines?.map((p) =>
              p.id === pipeline!.id && p.stage === oldName ? { ...p, stage: stage.name } : p,
            ),
          })),
        )
        for (const c of affectedContacts) {
          const updatedPipelines = (c.pipelines ?? []).map((p) =>
            p.id === pipeline!.id && p.stage === oldName ? { ...p, stage: stage.name } : p,
          )
          const input = contactPipelineInput(updatedPipelines)
          if (!input) {
            notifyError(`An existing pipeline has no stage. Select its stage before saving.`)
            continue
          }
          bg(contactStore.update(c.id!, { pipelines: input }))
        }
      }
      notifySuccess(`Stage ${isAdding ? `added` : `edited`} successfully`, `stage-update`)
    }
    closeModal()
  }

  const deleteStage: (name: string) => void = async (name) => {
    const pipeline = activePipelineRef.current
    // See updatePipelineStage: a system pipeline's stages are the Action Center boards'
    // columns, not this page's to remove.
    if (isSystemPipelineId(pipeline?.id)) {
      notifyError(
        `${pipeline?.name ?? `This pipeline`}’s stages are part of the Action Center and can’t be deleted`,
        `system-pipeline-stage-locked`,
      )
      closeModal()
      return
    }
    const updatedStages = (pipeline?.stages ?? [])
      .filter((s) => s.name !== name)
      .map((s, i) => ({ name: s.name, color: s.color ?? `pipelines-silver`, order: s.order ?? i + 1 }))
    const deleteResult = await pipelineStore.update(pipeline!.id!, {
      stages: updatedStages,
    })
    if (deleteResult.error) {
      debug(`pipeline`, `error`, deleteResult.error.message)
      notifyError(
        `There was an error deleting the stage ${name} from your pipeline. ${deleteResult.error.message}`,
        `stage-delete-error`,
      )
    } else {
      setActivePipeline({ ...pipeline!, stages: updatedStages })
      // Remove contacts from deleted stage in local state
      setContacts((prev) => prev.filter((c) => !c.pipelines?.some((p) => p.id === pipeline!.id && p.stage === name)))
      notifySuccess(`Stage deleted successfully`, `stage-delete`)
    }
    closeModal()
  }

  const addContactToPipeline = (contact: ContactJson, stage: StageType) => {
    if (!activePipeline || !contact?.id) return
    const pipelines = [...(contact.pipelines ?? []), { id: activePipeline.id!, stage: stage.name }]
    const input = contactPipelineInput(pipelines)
    if (!input) {
      notifyError(`An existing pipeline has no stage. Select its stage before saving.`)
      return
    }
    setContacts((prev) => [...prev, { ...contact, pipelines } as ContactJson])
    contactStore
      .update(contact.id, { pipelines: input })
      .then((result) => {
        if (result.error) {
          logError(`Error adding contact to pipeline`, result.error.message)
          setContacts((prev) => prev.filter((c) => c.id !== contact.id))
        } else {
          notifySuccess(`Contact added to pipeline`, `contact-added`)
        }
      })
      .catch((err) => {
        logError(`Error adding contact to pipeline`, err)
        setContacts((prev) => prev.filter((c) => c.id !== contact.id))
      })
  }

  const addContactsToPipeline = (selected: ContactJson[], stage: StageType) => {
    const pipeline = activePipelineRef.current
    if (!pipeline || selected.length === 0) return
    const withPipeline = selected.map(
      (c) => ({ ...c, pipelines: [...(c.pipelines ?? []), { id: pipeline.id!, stage: stage.name }] }) as ContactJson,
    )
    setContacts((prev) => [...prev, ...withPipeline])
    bg(
      Promise.all(
        withPipeline.map((c) => {
          const input = contactPipelineInput(c.pipelines ?? [])
          if (!input) {
            notifyError(`An existing pipeline has no stage. Select its stage before saving.`)
            setContacts((prev) => prev.filter((item) => item.id !== c.id))
            return Promise.resolve(false)
          }
          return contactStore
            .update(c.id!, { pipelines: input })
            .then((result) => {
              if (result.error) {
                logError(`contacts`, `error adding contact to pipeline`, result.error.message)
                setContacts((prev) => prev.filter((x) => x.id !== c.id))
                return false
              }
              return true
            })
            .catch((err) => {
              logError(`contacts`, `error adding contact to pipeline`, err)
              setContacts((prev) => prev.filter((x) => x.id !== c.id))
              return false
            })
        }),
      ).then((results) => {
        const added = results.filter(Boolean).length
        const failed = results.length - added
        if (added > 0) notifySuccess(`Added ${added} contact${added === 1 ? `` : `s`}`, `contacts-added`)
        if (failed > 0)
          notifyError(`${failed} contact${failed === 1 ? `` : `s`} couldn’t be added`, `contacts-add-failed`)
      }),
    )
  }

  const contactHandler = async (contactId: string) => {
    if (focusedContact?.id === contactId) return
    setFocusedContact(undefined)
    await contactStore.fetchOne(contactId)
    const contact = contactStore.current as (ContactJson | undefined) & { error?: string }
    if (contact?.error) {
      closeDrawerHandler()
      logError(`contacts`, `error loading contact with id ${contactId}`, contact.error)
      notifyError(`There was an error loading the contact`, `contact-load-error`)
    } else if (contact?.id) {
      let url = new URL(document.location.href)
      url.searchParams.set(`drawer`, contactId)
      history.replaceState({}, ``, url.toString())
      setOpenDrawer(true)
      setFocusedContact(contact)
    } else {
      debug([`pipelines`, `contacts`], `error loading contact with id ${contactId}`)
    }
  }

  const updateContactStage = (contactId: string, stage: StageType) => {
    let contact = contacts.find((c) => c.id === contactId)
    if (!contact) {
      notifyError(`Contact not found`, `contact-not-found`)
      return
    }
    let pipelineIndex = contact.pipelines?.findIndex((p) => p.id === activePipeline?.id)
    if (pipelineIndex === undefined || pipelineIndex < 0) {
      notifyError(`Contact not found in pipeline`, `contact-not-in-pipeline`)
      return
    }
    let prevStage = contact.pipelines![pipelineIndex].stage
    const pipelines = [...(contact.pipelines ?? [])]
    pipelines.splice(pipelineIndex, 1, { id: activePipeline!.id!, stage: stage.name })
    const input = contactPipelineInput(pipelines)
    if (!input) {
      notifyError(`An existing pipeline has no stage. Select its stage before saving.`)
      return
    }
    setContacts((prev) => {
      let updated = [...prev]
      let index = updated.findIndex((c) => c.id === contactId)
      if (index >= 0) updated[index] = { ...contact, pipelines } as ContactJson
      return updated
    })
    bg(
      contactStore.update(contactId, { pipelines: input }).then((result) => {
        if (result.error) {
          setContacts((prev) => {
            pipelines.splice(pipelineIndex!, 1, { id: activePipeline!.id!, stage: prevStage })
            let updated = [...prev]
            let index = updated.findIndex((c) => c.id === contactId)
            if (index >= 0) updated[index] = { ...contact, pipelines } as ContactJson
            return updated
          })
          notifyError(`There was an error updating the contact stage`, `contact-stage-update-error`)
          logError(`contacts`, `error updating contact stage`, result.error.message)
        }
      }),
    )
  }

  const removeContact = (contactId: string) => {
    let contact = contacts.find((c) => c.id === contactId)
    if (!contact) {
      notifyError(`Contact not found`)
      return
    }
    let pipelineIndex = contact.pipelines?.findIndex((p) => p.id === activePipeline?.id)
    if (pipelineIndex === undefined || pipelineIndex < 0) {
      notifyError(`Contact not found in pipeline`)
      return
    }
    const pipelines = (contact.pipelines ?? []).filter((p) => p.id !== activePipeline?.id)
    const input = contactPipelineInput(pipelines)
    if (!input) {
      notifyError(`An existing pipeline has no stage. Select its stage before saving.`)
      return
    }
    setContacts((prev) => prev.filter((c) => c.id !== contactId))
    bg(
      contactStore.update(contactId, { pipelines: input }).then((result) => {
        if (result.error) {
          notifyError(`There was an error removing the contact`, `contact-delete-error`)
          logError(`contacts`, `error removing contact from pipeline`, result.error.message)
          setContacts((prev) => [...prev, contact!])
        }
      }),
    )
  }

  const closeDrawerHandler = () => {
    debug(`pipelines`, `closeDrawerHandler`)
    setFocusedContact(undefined)
    setOpenDrawer(false)
    let url = new URL(document.location.href)
    url.searchParams.delete(`drawer`)
    history.replaceState({}, ``, url.toString())
  }

  const loadContact = (contact?: ContactJson) => {
    let url = new URL(document.location.href)
    setFocusedContact(undefined)
    let contactId = url.searchParams.get(`drawer`) || ``
    if (!contactId) {
      setOpenDrawer(false)
      return
    }
    bg(async () => {
      await contactStore.fetchOne(contactId)
      const response = contactStore.current as (ContactJson | undefined) & { error?: string }
      if (response?.id) {
        setFocusedContact(response)
      } else {
        notifyError(`Contact not found`)
        url.searchParams.delete(`drawer`)
        history.replaceState({}, ``, url.toString())
        setOpenDrawer(false)
      }
    })
  }

  useEffect(loadContact, [])

  const openCreateModal = () =>
    showModal(<AddPipeline createPipeline={createPipeline} />, {
      title: `Create New Pipeline`,
      isFullScreen: false,
      removePadding: true,
      useRadix: true,
    })

  return {
    me,
    workspace,
    contacts: displayedContacts,
    activePipeline,
    focusedContact,
    openDrawer,
    contactTraversal,
    createPipeline,
    updatePipelineStage,
    deleteStage,
    addContactToPipeline,
    addContactsToPipeline,
    contactHandler,
    updateContactStage,
    removeContact,
    closeDrawerHandler,
    loadContact,
    openCreateModal,
  }
}
